import { type FlexProps, type StyleProps } from "@chakra-ui/react"
import { type NavigateFunction } from "react-router"
import {
  isAndroid,
  isIOS,
  LOCAL_STORAGE_REGISTED_EMAIL,
  LOGGED_IN,
  REMEMBER_ME,
  TOKEN_NAME,
  URLForwardSlashSeparator,
  USER_DETAILS_NAME
} from "../../common/commonConstants"

export const isNewLoginUIEnabled = (): boolean =>
  import.meta.env.VITE_NEW_LOGIN_UI === "true" && !isAndroid() && !isIOS()
import { type UserDetailsInterface } from "../commons/commonTypes"
// import { Paths } from '../../common/commonConstants'
const commonStyles: StyleProps | Record<string, string> = {
  variant: "dropdown-button"
}
export const getSavedUserDetails = (): UserDetailsInterface | null => {
  const localStorageData = localStorage.getItem("user-details")
  const sessionStorageData = sessionStorage.getItem("user-details")
  if (localStorageData) {
    return JSON.parse(localStorageData)
  } else if (sessionStorageData) {
    return JSON.parse(sessionStorageData)
  }

  return null
}
export const levelStyles = {
  firstLevel: {
    buttonStyles: { ...commonStyles, px: "10px" },
    containerStyles: {}
  },
  secondLevel: {
    buttonStyles: { px: "10px", ...commonStyles, minWidth: "120px" },
    containerStyles: {}
  },
  thirdLevel: {
    buttonStyles: {
      px: "10px",
      fontWeight: "500",
      ...commonStyles,
      py: "0px",
      height: "auto",
      mt: "4px !important"
    },
    containerStyles: { ml: "4", borderLeft: "1px", pl: "4" }
  }
}
export const selectedSecondLevelStyles = {
  borderBottom: "1px",
  borderBottomColor: "white",
  borderRadius: 0,
  p: "4px"
}
export const subMenuContainerStyles: FlexProps = {
  p: "10px",
  bg: "primary",
  borderTop: "2px",
  borderColor: "white",
  position: "absolute",
  color: "white",
  zIndex: "100"
}
/**
 * Clears all authentication-related data from storage
 * This can be called from anywhere, including outside React Router context
 */
/**
 * Checks if a token exists (either in localStorage or sessionStorage)
 */
export const getToken = (): string | null => {
  const localStorageToken = localStorage.getItem(TOKEN_NAME)
  const sessionStorageToken = sessionStorage.getItem(TOKEN_NAME)
  return localStorageToken || sessionStorageToken || null
}

/**
 * Checks if a JWT token is valid (not expired)
 * Returns true if token exists and is not expired, false otherwise
 */
export const isTokenValid = (): boolean => {
  const token = getToken()
  if (!token) {
    return false
  }

  try {
    // Extract payload from JWT (second segment)
    const parts = token.split(".")
    if (parts.length !== 3) {
      return false
    }

    // Decode the payload
    const payload = JSON.parse(atob(parts[1]))

    // Check if token has expiration
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000)
      // Token is valid if expiration time is in the future (with 60 second buffer)
      return payload.exp > currentTime + 60
    }

    // If no expiration, consider it valid if it exists
    return true
  } catch (error) {
    // If decoding fails, token is invalid
    return false
  }
}

/**
 * Checks if remember me preference is set in localStorage
 */
export const isRememberMeSet = (): boolean => {
  return localStorage.getItem(REMEMBER_ME) === "true"
}

export const getTokenExpiry = (): number | null => {
  const token = getToken()
  if (!token) return null
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/**
 * Checks if a route is login protected
 * Only specific routes require authentication
 */
export const isProtectedRoute = (path: string): boolean => {
  return path.startsWith("/fmp/")
}

export const clearAuthData = (): void => {
  localStorage.removeItem(LOGGED_IN)
  localStorage.removeItem(TOKEN_NAME)
  sessionStorage.removeItem(TOKEN_NAME)
  localStorage.removeItem(USER_DETAILS_NAME)
  sessionStorage.removeItem(USER_DETAILS_NAME)
  localStorage.removeItem("user-fname")
  localStorage.removeItem("user-lname")
  localStorage.removeItem(LOCAL_STORAGE_REGISTED_EMAIL)
  // Don't clear return-url - we need it for redirect after login
  // Don't clear REMEMBER_ME - user preference should persist
}

export const logout = (navigate?: NavigateFunction): void => {
  clearAuthData()
  if (navigate) {
    navigate("/")
  }
}

export const sanitizeUrlSegment = (url: string, label?: string): string => {
  if (label?.includes("/")) {
    return url.replace(label, label.replaceAll("/", URLForwardSlashSeparator))
  }

  return url
}
